import {ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit} from "@angular/core";
import {
  AbstractControl, ControlValueAccessor,
  FormArray,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors, Validator,
  Validators,
} from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { SimpleLocation } from "../../../../platform-pages/src/lib/api/google-geocoding/google-geocoding.types";

export interface LocationOption {
  location: SimpleLocation;
}

@Component({
  selector: "mh-location-multiple-input",
  templateUrl: "./location-multiple-input.component.html",
  styleUrls: ["./location-multiple-input.component.less"],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LocationMultipleInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: LocationMultipleInputComponent, multi: true },
  ],
})
export class LocationMultipleInputComponent implements OnDestroy, ControlValueAccessor, OnInit, Validator {
  private destroy$ = new Subject<void>();

  @Input() set mark(isTouched: boolean) {
    if (isTouched) {
      this.form.markAllAsTouched();
    }
  }
  @Input()
  set value(value: Array<LocationOption>) {
    this._value = value;
    if (this.locationsFormArray) {
      this.initializeFormArray(value, false);
    }
  }
  get value(): Array<LocationOption> {
    return this._value;
  }
  private _value: Array<LocationOption> = [];
  disabled=false;

  form = this.formBuilder.group({
    locations: this.formBuilder.array([], [Validators.required, Validators.minLength(1)]),
  });

  get locationsFormArray(): FormArray {
    return this.form.controls["locations"] as FormArray;
  }

  constructor(private formBuilder: FormBuilder, private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {
    this.createForm();
  }

  addLocationOption(locationOption?: LocationOption | null, emitEvent: boolean = true) {
    if (!locationOption) locationOption = this.getBlankLocationOption();
    const languageForm = this.formBuilder.group({
      location: [locationOption.location],
    });
    this.locationsFormArray.push(languageForm, { emitEvent });
    if (emitEvent) this.notifyOfChange();
  }

  deleteLocationOption(index: number) {
    this.locationsFormArray.removeAt(index);
    this.notifyOfChange();
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  private createForm() {
    const currentLocations = this.value ? this.value : [];
    this.initializeFormArray(currentLocations);
    this.locationsFormArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: LocationOption[]) => {
      this._value = value.filter((locationOption) => !!locationOption.location?.postalCode);
      this.notifyOfChange();
    });
  }

  private initializeFormArray(locationOptions: Array<LocationOption>, emitEvent?: boolean) {
    this.locationsFormArray.clear({ emitEvent });
    if (locationOptions.length === 0) {
      this.addLocationOption(null, emitEvent);
    } else {
      locationOptions.forEach((locationOption) => {
        this.addLocationOption(locationOption, emitEvent);
      });
    }
    if (!emitEvent) this.changeDetectorRef.markForCheck();
  }

  private getBlankLocationOption(): LocationOption {
    return {location: { postalCode: undefined }};
  }

  private notifyOfChange() {
    if (this.onChange) {
      this._value = this.locationsFormArray.value;
      const outputValue = this.value.filter((langOption) => !!langOption.location.postalCode);
      this.onChange(outputValue.map((langOption => langOption.location)));
    }
  }

  onChange = (value: Array<SimpleLocation>) => {};
  onTouched = () => {};

  writeValue(value: Array<SimpleLocation>) {
    this._value = value.map(sl=> ({location: sl}));
    if (this.locationsFormArray) {
      this.initializeFormArray(this._value, false);
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    if(isDisabled) {
      this.locationsFormArray.disable({emitEvent: false, onlySelf: true});
    } else {
      this.locationsFormArray.enable({emitEvent: false, onlySelf: true});
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const locationControls = this.locationsFormArray.controls.map((control) => control.get("location"));
    const invalidLocationControls = locationControls.filter((control) => !control?.valid);

    const allInputsValid = invalidLocationControls.length == 0;

    return control.value && allInputsValid ? null : { required: true };
  }
}
